import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.module.css";
import { ko } from "date-fns/esm/locale";
import Swal from "sweetalert2";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

const initPtInfo = {
  age: "",
  birthDate: "",
  birthDueDate: "",
  chartNo: "",
  emrFamilyHistory: "",
  pastHistory: "",
  patientId: "",
  patientName: "",
  pregnantWeeks: "",
  surgeryType: "",
  isFirstChildbirth: "",
  emrPresentIllness: "",
  naturalDelivery: "",
  cesareanSection: "",
  // 'bunmanPresentIllness': ''
};

const initPtVital = {
  weight: "",
  height: "",
  bmi: "",
  minBloodPressure: "",
  maxBloodPressure: "",
  temperature: "",
  bloodGlucose: "",
  pulseRate: "",
};

const initObHis = {
  patientId: "",
  lastMensDate: "",
};

const RegPatient = forwardRef(({ patientId, patientVital, patientInfo, patientObHistory, onPatientInfoChange, onPatientVSChange, onPatientObHistoryChange }, ref) => {
  const [isOpenPresent, setIsOpenPresent] = useState(false); //현병력 모달
  const [isOpenFamily, setIsOpenFamily] = useState(false); //가족력 모달
  const [isOpenPast, setIsOpenPast] = useState(false); //과거력 모달
  const [isReadOnly, setIsReadOnly] = useState(patientInfo.isFirstChildbirth === true);

  const [checkedOptions, setCheckedOptions] = useState({ bunmanFamilyHistory: [], bunmanPastHistory: [], bunmanPresentHistory: [] });

  const openDiseuseInputPopupByName = (popupType) => {
    if (popupType === "emrFamilyHistory") {
      setIsOpenFamily(true);
    } else if (popupType === "pastHistory") {
      setIsOpenPast(true);
    } else {
      setIsOpenPresent(true);
    }
  };

  // 모달 닫기 함수
  const closePresentModal = () => {
    setIsOpenPresent(false);
  };

  const closePastModal = () => {
    setIsOpenPast(false);
  };

  const closeFamilyModal = () => {
    setIsOpenFamily(false);
  };

  const saveInput = (e) => {
    if (e.target.name == "bunmanPresentHistory") {
      const emrPresentIllnessString = checkedOptions.bunmanPresentHistory.length > 0 ? checkedOptions.bunmanPresentHistory.join(", ") : "";
      setEditPatientInfo((prevState) => ({
        ...prevState,
        emrPresentIllness: emrPresentIllnessString,
      }));
      onPatientInfoChange("emrPresentIllness", emrPresentIllnessString);
      setIsOpenPresent(false);
    } else if (e.target.name == "bunmanFamilyHistory") {
      const emrFamilyHistoryString = checkedOptions.bunmanFamilyHistory.length > 0 ? checkedOptions.bunmanFamilyHistory.join(", ") : "";
      setEditPatientInfo((prevState) => ({
        ...prevState,
        emrFamilyHistory: emrFamilyHistoryString,
      }));
      onPatientInfoChange("emrFamilyHistory", emrFamilyHistoryString);
      setIsOpenFamily(false);
    } else {
      const pastHistoryString = checkedOptions.bunmanPastHistory.length > 0 ? checkedOptions.bunmanPastHistory.join(", ") : "";
      setEditPatientInfo((prevState) => ({
        ...prevState,
        pastHistory: pastHistoryString,
      }));
      onPatientInfoChange("pastHistory", pastHistoryString);
      setIsOpenPast(false);
    }
  };

  // 체크박스 변경 이벤트 핸들러
  const handleCheckboxChange = (value, type) => {
    let updatedCheckedOptions = [...checkedOptions[type]];

    // '' 값을 선택한 경우, 다른 값들을 모두 제거합니다.
    if (value === "") {
      updatedCheckedOptions = [""];
    } else {
      const index = updatedCheckedOptions.indexOf(value);

      if (index === -1) {
        // 다른 값이 선택된 경우, ''를 배열에서 제거합니다.
        const emptyIndex = updatedCheckedOptions.indexOf("");
        if (emptyIndex !== -1) {
          updatedCheckedOptions.splice(emptyIndex, 1);
        }

        updatedCheckedOptions.push(value);
      } else {
        updatedCheckedOptions.splice(index, 1);
      }
    }

    // 변경된 배열을 상태에 반영합니다.
    setCheckedOptions((prevState) => ({
      ...prevState,
      [type]: updatedCheckedOptions,
    }));
  };

  // 분만 예정일을 입력 받아 임신 주수와 마지막 생리일을 계산하는 함수
  const calculateWeekAndLastMensDate = (birthDueDate) => {
    let inputbirthDueDate = birthDueDate;

    if (birthDueDate.length === 8) {
      inputbirthDueDate = birthDueDate.substring(0, 4) + "-" + birthDueDate.substring(4, 6) + "-" + birthDueDate.substring(6, 8);
    }

    // 분만 예정일을 Date 객체로 변환 birthDueDate
    const birthDueDateObj = new Date(inputbirthDueDate);

    // 분만 예정일로부터 280일(임신 기간)을 빼서 마지막 생리일을 계산
    const lastMensDateObj = new Date(birthDueDateObj.getTime() - 280 * 24 * 60 * 60 * 1000);
    const lastMensDateFormatted = lastMensDateObj.toISOString().split("T")[0]; // YYYY-MM-DD 형식으로 변환

    // 현재 날짜를 기준으로 임신 주수를 계산
    const currentDateObj = new Date();
    const daysUntilDueDate = Math.floor((birthDueDateObj - currentDateObj) / (1000 * 60 * 60 * 24));
    const currentWeek = Math.floor((280 - daysUntilDueDate) / 7);

    return { lastMensDate: lastMensDateFormatted, pregnantWeeks: currentWeek };
  };

  // YYYYMMDD 형식의 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
  const formatToYYYYMMDD = (dateString) => {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return `${year}-${month}-${day}`;
  };

  const [editPatientInfo, setEditPatientInfo] = useState(() => {
    const initialPregnantWeeks = patientInfo.birthDueDate ? calculateWeekAndLastMensDate(patientInfo.birthDueDate).pregnantWeeks : "";

    return {
      ...patientInfo,
      pregnantWeeks: initialPregnantWeeks,
      birthDueDate: patientInfo.birthDueDate ? formatToYYYYMMDD(patientInfo.birthDueDate) : "",
    };
  });

  useEffect(() => {
    // patientInfo가 변경될 때마다 실행됩니다.
    if (editPatientInfo) {
      // 가족력, 과거력, 현병력을 배열로 변환합니다.
      const bunmanFamilyHistory = patientInfo.emrFamilyHistory ? patientInfo.emrFamilyHistory.split(",").map((item) => item.trim()) : [""];
      const bunmanPastHistory = patientInfo.pastHistory ? patientInfo.pastHistory.split(",").map((item) => item.trim()) : [""];
      const bunmanPresentHistory = patientInfo.emrPresentIllness ? patientInfo.emrPresentIllness.split(",").map((item) => item.trim()) : [""];

      // 배열로 변환된 값으로 checkedOptions를 업데이트합니다.
      setCheckedOptions({
        bunmanFamilyHistory,
        bunmanPastHistory,
        bunmanPresentHistory,
      });
    }
  }, [editPatientInfo]);

  useEffect(() => {
    // 임신 주수가 변경될 때마다 부모 컴포넌트로 전달
    onPatientInfoChange("pregnantWeeks", editPatientInfo.pregnantWeeks);
  }, [editPatientInfo.pregnantWeeks]);

  const [editPatientVital, setEditPatientVital] = useState(patientVital);

  const [editPatientObHistory, setEditPatientObHistory] = useState({
    ...patientObHistory,
    lastMensDate: patientObHistory.lastMensDate ? formatToYYYYMMDD(patientObHistory.lastMensDate) : "",
  });

  const handlePtInfoInputChange = (e) => {
    let { name, value } = e.target;

    if (name === "birthDueDate" || name === "birthDate") {
      let formattedValue = value.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3");
      value = formattedValue;
    }

    // 초산 여부가 변경될 때 자연분만 및 제왕절개 값 설정
    if (name === "isFirstChildbirth") {
      if (value === "true") {
        setEditPatientInfo((prevState) => {
          const updatedInfo = {
            ...prevState,
            naturalDelivery: 0,
            cesareanSection: 0,
            isFirstChildbirth: value,
          };
          onPatientInfoChange("isFirstChildbirth", value);
          onPatientInfoChange("naturalDelivery", 0);
          onPatientInfoChange("cesareanSection", 0);
          setIsReadOnly(true);
          return updatedInfo;
        });
      } else {
        setEditPatientInfo((prevState) => {
          const updatedInfo = {
            ...prevState,
            isFirstChildbirth: value,
          };
          onPatientInfoChange("isFirstChildbirth", value);
          setIsReadOnly(false);
          return updatedInfo;
        });
      }
    } else {
      setEditPatientInfo((prevState) => {
        const updatedInfo = {
          ...prevState,
          [name]: value,
        };
        onPatientInfoChange(name, value);
        return updatedInfo;
      });
    }
  };

  const handlePtVSInputChange = (e) => {
    const { name, value } = e.target;
    if (value < 0) {
      Swal.fire({
        icon: "warning",
        title: "입력값 오류",
        text: "0보다 작은 값을 입력할 수 없습니다.",
        customClass: {
          popup: "custom-swal-popup",
        },
      });
      setEditPatientVital((prevState) => ({
        ...prevState,
        [name]: "",
      }));
      onPatientVSChange(name, "");
    } else {
      setEditPatientVital((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      onPatientVSChange(name, value);
    }
  };

  //나이 계산
  const calculateAge = (birthdate) => {
    // 넘어온 데이터가 날짜인지 확인
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      throw new Error("Invalid birthdate format"); // 날짜 형식이 아니면 에러를 던집니다.
    }

    // 생년월일을 Date 객체로 변환합니다.
    const birthDateObj = new Date(birthdate);
    const currentDate = new Date();

    // 생일이 지났는지 여부를 확인하여 나이를 계산합니다.
    let age = currentDate.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = currentDate.getMonth() - birthDateObj.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDateObj.getDate())) {
      age--; // 생일이 지나지 않았다면 나이를 1살 줄입니다.
    }

    return age;
  };

  //생년월일이 입력되면 나이를 자동 계산
  const handleCalculateAgeBlur = () => {
    let birthDate = editPatientInfo.birthDate;
    const isValidDate = isValidDateFormat(birthDate);

    if (isValidDate) {
      const currentDate = new Date().toISOString().split("T")[0];
      if (birthDate > currentDate) {
        // 입력한 날짜가 오늘 날짜보다 이후인 경우
        Swal.fire({
          icon: "warning",
          title: "날짜 입력 오류",
          text: "생년월일은 오늘 날짜보다 이전이어야 합니다.",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      } else {
        const age = calculateAge(birthDate);
        setEditPatientInfo((prevState) => ({
          ...prevState,
          age: age,
        }));
        onPatientInfoChange("age", age);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "날짜 입력 오류",
        text: "올바른 날짜 형식이 아닙니다. YYYY-MM-DD 형식으로 입력하거나 날짜를 선택해주세요.",
        customClass: {
          popup: "custom-swal-popup",
        },
      });
      setEditPatientInfo((prevState) => ({
        ...prevState,
        age: "",
      }));
      onPatientInfoChange("age", "");
    }
  };

  //키와 몸무게가 모두 입력되었다면 BMI는 자동 계산
  const handleCalculateBMIBlur = () => {
    if (editPatientVital.height && editPatientVital.weight) {
      const bmi = calculateBMI(editPatientVital.height, editPatientVital.weight);
      setEditPatientVital((prevState) => ({
        ...prevState,
        bmi: bmi,
      }));
      onPatientVSChange("bmi", bmi);
    }
  };

  // BMI(체질량 지수) 계산
  const calculateBMI = (height, weight) => {
    // 키와 몸무게가 숫자인지 확인
    if (!isNaN(height) && !isNaN(weight)) {
      const heightInMeter = height / 100;
      const bmi = weight / (heightInMeter * heightInMeter);
      return Math.round(bmi * 100) / 100;
    }
  };

  //마지막 생리일을 사용하여 출산 예정일, 임신 주수 계산
  const handleLastMensDateBlur = () => {
    const inputLastMensDate = editPatientObHistory.lastMensDate;
    const isValidDate = isValidDateFormat(inputLastMensDate);

    if (isValidDate) {
      const currentDate = new Date().toISOString().split("T")[0];
      if (inputLastMensDate > currentDate) {
        // 입력한 날짜가 오늘 날짜보다 이후인 경우
        Swal.fire({
          icon: "warning",
          title: "날짜 입력 오류",
          text: "마지막 생리일은 오늘 날짜보다 이전이어야 합니다.",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      } else {
        const { birthDueDate, pregnantWeeks } = calculateDueDateAndWeek(inputLastMensDate);
        // 출산 예정일과 임신 주수를 개별적으로 설정하고 부모 컴포넌트로 전달
        setEditPatientInfo((prevState) => ({
          ...prevState,
          birthDueDate: birthDueDate,
          pregnantWeeks: pregnantWeeks,
        }));

        // 부모 컴포넌트로 출산 예정일과 임신 주수를 전달
        onPatientInfoChange("birthDueDate", birthDueDate);
        onPatientInfoChange("pregnantWeeks", pregnantWeeks);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "날짜 입력 오류",
        text: "올바른 날짜 형식이 아닙니다. YYYY-MM-DD 형식으로 입력하거나 날짜를 선택해주세요.",
        customClass: {
          popup: "custom-swal-popup",
        },
      });

      if (inputLastMensDate === "") {
        setEditPatientInfo((prevState) => ({
          ...prevState,
          birthDueDate: "",
          pregnantWeeks: "",
        }));

        onPatientInfoChange("birthDueDate", "");
        onPatientInfoChange("pregnantWeeks", "");
      }
    }
  };

  // 마지막 생리일을 입력받아 분만 예정일과 현재 임신 주수를 계산하는 함수
  const calculateDueDateAndWeek = (lastMensDate) => {
    const lastMensDateObj = new Date(lastMensDate);
    const dueDateObj = new Date(lastMensDateObj.getTime() + 280 * 24 * 60 * 60 * 1000);
    const dueDateFormatted = dueDateObj.toISOString().split("T")[0]; // YYYY-MM-DD 형식으로 변환
    const currentDateObj = new Date();
    const daysUntilDueDate = Math.floor((dueDateObj - currentDateObj) / (1000 * 60 * 60 * 24));
    const currentWeek = Math.floor((280 - daysUntilDueDate) / 7);

    return { birthDueDate: dueDateFormatted, pregnantWeeks: currentWeek };
  };

  const handleBirthDueDateBlur = () => {
    const inputBirthDueDate = editPatientInfo.birthDueDate;
    const isValidDate = isValidDateFormat(inputBirthDueDate);

    if (isValidDate) {
      const currentDate = new Date().toISOString().split("T")[0];
      const { lastMensDate, pregnantWeeks } = calculateWeekAndLastMensDate(inputBirthDueDate);

      if (currentDate > inputBirthDueDate) {
        Swal.fire({
          icon: "warning",
          title: "날짜 입력 오류",
          text: "분만예정일은 오늘 날짜보다 앞이어야 합니다.",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      } else {
        //마지막 생리일과 임신 주수를 설정하고 부모 컴포넌트로 전달
        setEditPatientInfo((prevState) => ({
          ...prevState,
          pregnantWeeks: pregnantWeeks,
        }));

        setEditPatientObHistory((prevState) => ({
          ...prevState,
          lastMensDate: lastMensDate,
        }));

        onPatientInfoChange("pregnantWeeks", pregnantWeeks);
        onPatientObHistoryChange("lastMensDate", lastMensDate);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "날짜 입력 오류",
        text: "올바른 날짜 형식이 아닙니다. YYYY-MM-DD 형식으로 입력하거나 날짜를 선택해주세요.",
        customClass: {
          popup: "custom-swal-popup",
        },
      });

      if (inputBirthDueDate === "") {
        setEditPatientInfo((prevState) => ({
          ...prevState,
          pregnantWeeks: "",
        }));

        setEditPatientObHistory((prevState) => ({
          ...prevState,
          lastMensDate: "",
        }));

        onPatientInfoChange("lastMensDate", "");
        onPatientInfoChange("pregnantWeeks", "");
      }
    }
  };

  //YYYY-MM-DD 형식 검사
  const isValidDateFormat = (dateString) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD 형식을 나타내는 정규식입니다.

    if (!regex.test(dateString)) {
      return false; // 형식이 맞지 않으면 유효하지 않은 날짜 형식으로 간주합니다.
    }

    const year = parseInt(dateString.substr(0, 4), 10);
    const month = parseInt(dateString.substr(5, 2), 10);
    const day = parseInt(dateString.substr(8, 2), 10);

    // 날짜 객체를 생성하여 유효성을 확인합니다.
    const dateObject = new Date(year, month - 1, day); // month는 0부터 시작하므로 -1 처리합니다.
    const isValidDate = dateObject.getFullYear() === year && dateObject.getMonth() === month - 1 && dateObject.getDate() === day;

    return isValidDate;
  };

  const clearFn = () => {
    setEditPatientInfo(initPtInfo);
    setEditPatientVital(initPtVital);
    setEditPatientObHistory(initObHis);
  };

  // useImperativeHandle을 사용하여 clearData 함수를 부모 컴포넌트로 노출
  useImperativeHandle(ref, () => ({
    clearData: clearFn,
  }));

  return (
    <div className="nurse-reg-div">
      <Modal
        isOpen={isOpenPresent}
        contentLabel="현병력 선택"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid #ccc",
            background: "white",
            borderRadius: "4px",
            width: "380px",
            height: "163px",
            padding: "8px",
            fontWeight: "400",
          },
        }}
      >
        <div className="tab-area">
          <div className={"tab selected-tab"}>현병력 선택</div>
        </div>
        {
          <div className="bfp-body-container">
            <div className="chklist-choice-container">
              <input
                type="checkbox"
                name="choice-illness"
                id="당뇨"
                checked={checkedOptions.bunmanPresentHistory.includes("당뇨")}
                onChange={() => handleCheckboxChange("당뇨", "bunmanPresentHistory")}
              />
              <label htmlFor="당뇨" className="nurse-cursor">
                당뇨
              </label>
              <input type="checkbox" name="choice-illness" id="없음" checked={checkedOptions.bunmanPresentHistory.includes("")} onChange={() => handleCheckboxChange("", "bunmanPresentHistory")} />
              <label htmlFor="없음" className="nurse-cursor">
                없음
              </label>
            </div>
            <div className="csc-button-container">
              <button onClick={saveInput} name="bunmanPresentHistory">
                현병력 적용
              </button>
              <button onClick={closePresentModal} style={{ backgroundColor: "red" }}>
                닫기
              </button>
            </div>
          </div>
        }
      </Modal>

      <Modal
        isOpen={isOpenFamily}
        contentLabel="가족력 선택"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid #ccc",
            background: "white",
            borderRadius: "4px",
            width: "380px",
            height: "163px",
            padding: "8px",
            fontWeight: "400",
          },
        }}
      >
        <div className="tab-area">
          <div className={"tab selected-tab"}>가족력 선택</div>
        </div>
        {
          <div className="bfp-body-container">
            <div className="chklist-choice-container">
              <input
                type="checkbox"
                name="choice-illness"
                id="전자간증"
                checked={checkedOptions.bunmanFamilyHistory.includes("전자간증")}
                onChange={() => handleCheckboxChange("전자간증", "bunmanFamilyHistory")}
              />
              <label htmlFor="전자간증" className="nurse-cursor">
                전자간증
              </label>
              <input
                type="checkbox"
                name="choice-illness"
                id="고혈압"
                checked={checkedOptions.bunmanFamilyHistory.includes("고혈압")}
                onChange={() => handleCheckboxChange("고혈압", "bunmanFamilyHistory")}
              />
              <label htmlFor="고혈압" className="nurse-cursor">
                고혈압
              </label>
              <input
                type="checkbox"
                name="choice-illness"
                id="당뇨"
                checked={checkedOptions.bunmanFamilyHistory.includes("당뇨")}
                onChange={() => handleCheckboxChange("당뇨", "bunmanFamilyHistory")}
              />
              <label htmlFor="당뇨" className="nurse-cursor">
                당뇨
              </label>
            </div>
            <div className="chklist-choice-container">
              <input
                type="checkbox"
                name="choice-illness"
                id="신장질환"
                checked={checkedOptions.bunmanFamilyHistory.includes("신장질환")}
                onChange={() => handleCheckboxChange("신장질환", "bunmanFamilyHistory")}
              />
              <label htmlFor="신장질환" className="nurse-cursor">
                신장질환
              </label>
              <input
                type="checkbox"
                name="choice-illness"
                id="자가면역질환"
                checked={checkedOptions.bunmanFamilyHistory.includes("자가면역질환")}
                onChange={() => handleCheckboxChange("자가면역질환", "bunmanFamilyHistory")}
              />
              <label htmlFor="자가면역질환" className="nurse-cursor">
                자가면역질환
              </label>
              <input type="checkbox" name="choice-illness" id="없음" checked={checkedOptions.bunmanFamilyHistory.includes("")} onChange={() => handleCheckboxChange("", "bunmanFamilyHistory")} />
              <label htmlFor="없음" className="nurse-cursor">
                없음
              </label>
            </div>
            <div className="csc-button-container">
              <button onClick={saveInput} name="bunmanFamilyHistory">
                가족력 적용
              </button>
              <button onClick={closeFamilyModal} style={{ backgroundColor: "red" }}>
                닫기
              </button>
            </div>
          </div>
        }
      </Modal>

      <Modal
        isOpen={isOpenPast}
        contentLabel="과거력 선택"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid #ccc",
            background: "white",
            borderRadius: "4px",
            width: "380px",
            height: "163px",
            padding: "8px",
            fontWeight: "400",
          },
        }}
      >
        <div className="tab-area">
          <div className={"tab selected-tab"}>과거력 선택</div>
        </div>
        {
          <div className="bfp-body-container">
            <div className="chklist-choice-container">
              <input
                type="checkbox"
                name="choice-illness"
                id="항문 헤르페스"
                checked={checkedOptions.bunmanPastHistory.includes("항문 헤르페스")}
                onChange={() => handleCheckboxChange("항문 헤르페스", "bunmanPastHistory")}
              />
              <label htmlFor="항문 헤르페스" className="nurse-cursor">
                항문 헤르페스
              </label>
              <input
                type="checkbox"
                name="choice-illness"
                id="고혈압성질환"
                checked={checkedOptions.bunmanPastHistory.includes("고혈압성질환")}
                onChange={() => handleCheckboxChange("고혈압성질환", "bunmanPastHistory")}
              />
              <label htmlFor="고혈압성질환" className="nurse-cursor">
                고혈압성질환
              </label>
              <input
                type="checkbox"
                name="choice-illness"
                id="만성신장질환"
                checked={checkedOptions.bunmanPastHistory.includes("만성신장질환")}
                onChange={() => handleCheckboxChange("만성신장질환", "bunmanPastHistory")}
              />
              <label htmlFor="만성신장질환" className="nurse-cursor">
                만성신장질환
              </label>
            </div>
            <div className="chklist-choice-container">
              <input
                type="checkbox"
                name="choice-illness"
                id="자가면역질환"
                checked={checkedOptions.bunmanPastHistory.includes("자가면역질환")}
                onChange={() => handleCheckboxChange("자가면역질환", "bunmanPastHistory")}
              />
              <label htmlFor="자가면역질환" className="nurse-cursor">
                자가면역질환
              </label>
              <input
                type="checkbox"
                name="choice-illness"
                id="만성고혈압"
                checked={checkedOptions.bunmanPastHistory.includes("만성고혈압")}
                onChange={() => handleCheckboxChange("만성고혈압", "bunmanPastHistory")}
              />
              <label htmlFor="만성고혈압" className="nurse-cursor">
                만성고혈압
              </label>
              <input type="checkbox" name="choice-illness" id="없음" checked={checkedOptions.bunmanPastHistory.includes("")} onChange={() => handleCheckboxChange("", "bunmanPastHistory")} />
              <label htmlFor="없음" className="nurse-cursor">
                없음
              </label>
            </div>
            <div className="csc-button-container">
              <button onClick={saveInput} name="bunmanPastHistory">
                과거력 적용
              </button>
              <button onClick={closePastModal} style={{ backgroundColor: "red" }}>
                닫기
              </button>
            </div>
          </div>
        }
      </Modal>

      <div className="nurse-patient-info">
        <h3 className="nurse-pt-title">{patientId ? "환자 정보" : "환자 신규 등록"}</h3>
        <div className="nurse-info-table-container">
          <div className="nurse-content-div">
            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell">이름</div>
                <div className="info-cell-white">
                  <input
                    type="text"
                    className="nurse-info-input-cell"
                    name="patientName"
                    value={editPatientInfo && editPatientInfo.patientName ? editPatientInfo.patientName : ""}
                    onChange={handlePtInfoInputChange}
                  />
                </div>
                <div className="nurse-info-cell">차트 번호</div>
                <div className="info-cell-white">
                  <input
                    type="text"
                    className="nurse-info-input-cell"
                    name="chartNo"
                    value={editPatientInfo && editPatientInfo.chartNo ? editPatientInfo.chartNo : ""}
                    onChange={handlePtInfoInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell">생년 월일</div>
                <div className="info-cell-white">
                  <DatePicker
                    maxDate={new Date()}
                    className="nurse-info-input-cell"
                    placeholderText={"YYYY-MM-DD"}
                    selected={editPatientInfo && editPatientInfo.birthDate ? new Date(editPatientInfo.birthDate) : null}
                    locale={ko}
                    onBlur={handleCalculateAgeBlur}
                    onChange={(date) => {
                      const formattedDate = date ? date.toISOString().split("T")[0] : "";
                      setEditPatientInfo((prevState) => ({
                        ...prevState,
                        birthDate: formattedDate,
                      }));
                      onPatientInfoChange("birthDate", formattedDate);
                    }}
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
                <div className="nurse-info-cell">나이</div>
                <div className="info-cell-white">
                  <input
                    type="text"
                    className="nurse-info-input-cell"
                    name="age"
                    readOnly
                    value={editPatientInfo && editPatientInfo.age ? editPatientInfo.age : ""}
                    onChange={handlePtInfoInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell">자연분만</div>
                <div className="info-cell-white">
                  <input
                    type="number"
                    className="nurse-info-input-cell"
                    name="naturalDelivery"
                    value={editPatientInfo && editPatientInfo.naturalDelivery ? editPatientInfo.naturalDelivery : 0}
                    onChange={handlePtInfoInputChange}
                    placeholder="횟수를 입력해주세요."
                    min="0"
                    readOnly={isReadOnly}
                  />
                </div>
                <div className="nurse-info-cell">제왕절개</div>
                <div className="info-cell-white">
                  <input
                    type="number"
                    className="nurse-info-input-cell"
                    name="cesareanSection"
                    value={editPatientInfo && editPatientInfo.cesareanSection ? editPatientInfo.cesareanSection : 0}
                    onChange={handlePtInfoInputChange}
                    placeholder="횟수를 입력해주세요."
                    min="0"
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>

            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell" name="lastMensDate">
                  마지막 생리일
                </div>
                <div className="info-cell-white">
                  <DatePicker
                    locale={ko}
                    placeholderText={"YYYY-MM-DD"}
                    selected={editPatientObHistory && editPatientObHistory.lastMensDate ? new Date(editPatientObHistory.lastMensDate) : null}
                    onChange={(date) => {
                      const formattedDate = date ? date.toISOString().split("T")[0] : "";
                      setEditPatientObHistory((prevState) => ({
                        ...prevState,
                        lastMensDate: formattedDate,
                      }));
                      onPatientObHistoryChange("lastMensDate", formattedDate);
                    }}
                    onBlur={handleLastMensDateBlur}
                    dateFormat="yyyy-MM-dd"
                    className="nurse-info-input-cell"
                    name="lastMensDate"
                    // maxDate={new Date()}
                  />
                </div>
                <div className="nurse-info-cell">분만 예정일</div>
                <div className="info-cell-white">
                  <DatePicker
                    locale={ko}
                    selected={editPatientInfo && editPatientInfo.birthDueDate ? new Date(editPatientInfo.birthDueDate) : ""}
                    placeholderText={"YYYY-MM-DD"}
                    className="nurse-info-input-cell"
                    name="birthDueDate"
                    dateFormat="yyyy-MM-dd"
                    onBlur={handleBirthDueDateBlur}
                    onChange={(date) => {
                      const formattedDate = date ? date.toISOString().split("T")[0] : "";
                      setEditPatientInfo((prevState) => ({
                        ...prevState,
                        birthDueDate: formattedDate,
                      }));
                      onPatientInfoChange("birthDueDate", formattedDate);
                    }}
                    // minDate={new Date()}
                  />
                </div>
              </div>
            </div>

            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell">임신 주수</div>
                <div className="info-cell-white">
                  <input
                    type="text"
                    className="nurse-info-input-cell"
                    name="pregnantWeeks"
                    readOnly
                    value={editPatientInfo && editPatientInfo.pregnantWeeks ? editPatientInfo.pregnantWeeks : ""}
                    onChange={handlePtInfoInputChange}
                  />
                </div>
                <div className="nurse-info-cell">
                  현병력
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="nurse-magnifyGlass-icon nurse-cursor" onClick={() => openDiseuseInputPopupByName("emrPresentIllness")} />
                </div>
                <div className="info-cell-white">
                  <span className="nurse-info-span-cell" style={{ display: "block" }}>
                    {editPatientInfo && editPatientInfo.emrPresentIllness ? (
                      editPatientInfo.emrPresentIllness.split(",").map((item, index) => (
                        <span key={index}>
                          {item}
                          <br />
                        </span>
                      ))
                    ) : (
                      <span>없음</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell nurse-vertical-align-middle">
                  가족력
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="nurse-magnifyGlass-icon nurse-cursor" onClick={() => openDiseuseInputPopupByName("emrFamilyHistory")} />
                </div>
                <div className="info-cell-white">
                  <span className="nurse-info-span-cell nurse-margin-top-bottom-5" style={{ display: "block" }}>
                    {editPatientInfo && editPatientInfo.emrFamilyHistory ? (
                      editPatientInfo.emrFamilyHistory.split(",").map((item, index) => (
                        <span key={index}>
                          {item}
                          <br />
                        </span>
                      ))
                    ) : (
                      <span>없음</span>
                    )}
                  </span>
                </div>
                <div className="nurse-info-cell nurse-vertical-align-middle">
                  과거력
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="nurse-magnifyGlass-icon nurse-cursor" onClick={() => openDiseuseInputPopupByName("pastHistory")} />
                </div>
                <div className="info-cell-white">
                  <span className="nurse-info-span-cell nurse-margin-top-bottom-5" style={{ display: "block" }}>
                    {editPatientInfo && editPatientInfo.pastHistory ? (
                      editPatientInfo.pastHistory.split(",").map((item, index) => (
                        <span key={index}>
                          {item}
                          <br />
                        </span>
                      ))
                    ) : (
                      <span>없음</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="nurse-info-custom-table">
              <div className="row header3">
                <div className="nurse-info-cell">초산여부</div>
                <div className="info-cell-white">
                  <select
                    className="nurse-select"
                    name="isFirstChildbirth"
                    value={editPatientInfo && editPatientInfo.isFirstChildbirth !== null ? editPatientInfo.isFirstChildbirth : ""}
                    onChange={handlePtInfoInputChange}
                  >
                    <option value={""}>선택</option>
                    <option value={"true"}>초산</option>
                    <option value={"false"}>경산</option>
                  </select>
                </div>
                {/* 빈 div로 공간만 차지하게 함 */}
                <div className="nurse-info-cell" style={{ visibility: "hidden" }}></div>
                <div className="info-cell-white" style={{ visibility: "hidden" }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="nurse-patient-vital">
        <h3 className="nurse-pt-title">바이탈</h3>
        <div className="nurse-vital-table-container">
          <div className="nurse-content-div">
            <div className="nurse-custom-table">
              <div className="row header3">
                <div className="nurse-vital-cell">키(cm)</div>
                <div className="nurse-vital-cell">몸무게(kg)</div>
                <div className="nurse-vital-cell">BMI</div>
                <div className="nurse-vital-cell">체온(℃)</div>
              </div>
              <div className="row">
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="height"
                    value={editPatientVital && editPatientVital.height ? editPatientVital.height : ""}
                    onChange={handlePtVSInputChange}
                    onBlur={handleCalculateBMIBlur}
                    min={0}
                  />
                </div>
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="weight"
                    value={editPatientVital && editPatientVital.weight ? editPatientVital.weight : ""}
                    onChange={handlePtVSInputChange}
                    onBlur={handleCalculateBMIBlur}
                    min={0}
                  />
                </div>
                <div className="nurse-vital-cell">
                  <input type="number" className="vs-input" name="bmi" value={editPatientVital && editPatientVital.bmi ? editPatientVital.bmi : ""} readOnly onChange={handlePtVSInputChange} min={0} />
                </div>
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="temperature"
                    value={editPatientVital && editPatientVital.temperature ? editPatientVital.temperature : ""}
                    onChange={handlePtVSInputChange}
                    min={0}
                  />
                </div>
              </div>
            </div>
            <div className="nurse-custom-table">
              <div className="row header3">
                <div className="nurse-vital-cell">맥박(회)</div>
                <div className="nurse-vital-cell">수축기 혈압</div>
                <div className="nurse-vital-cell">이완기 혈압</div>
                <div className="nurse-vital-cell">혈당(mg/dL)</div>
              </div>
              <div className="row">
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="pulseRate"
                    value={editPatientVital && editPatientVital.pulseRate ? editPatientVital.pulseRate : ""}
                    onChange={handlePtVSInputChange}
                    min={0}
                  />
                </div>
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="maxBloodPressure"
                    value={editPatientVital && editPatientVital.maxBloodPressure ? editPatientVital.maxBloodPressure : ""}
                    onChange={handlePtVSInputChange}
                    min={0}
                  />
                </div>
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="minBloodPressure"
                    value={editPatientVital && editPatientVital.minBloodPressure ? editPatientVital.minBloodPressure : ""}
                    onChange={handlePtVSInputChange}
                    min={0}
                  />
                </div>
                <div className="nurse-vital-cell">
                  <input
                    type="number"
                    className="vs-input"
                    name="bloodGlucose"
                    value={editPatientVital && editPatientVital.bloodGlucose ? editPatientVital.bloodGlucose : ""}
                    onChange={handlePtVSInputChange}
                    min={0}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default RegPatient;
